import { ExportOutlined } from '@ant-design/icons';
import { Breadcrumb, Tabs, theme, Tooltip, Typography } from 'antd';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CrmType, crmTypeMeta } from '../../types/crmTypes';
import { HWButton } from '../shared/StyledButton';
import { Agent } from '../../types';

type TabKey = 'settings' | 'sms' | 'onboarding' | 'leads' | 'twilio';
interface SectionTab {
  key: TabKey;
  label: string;
  to: string;
}

interface ClientNavigationProps {
  clientId?: string;
  numericClientId?: number;
  clientName?: string;
  activeTab?: TabKey;
  crmLink?: string;
  crmType?: CrmType;
  isSingleAgent?: boolean;
  hasCrmCredentials?: boolean;
  agent?: Agent;
  /** Per-agent drill-down view: hide the client section tabs even before
   *  `agent` has loaded (otherwise tabs flash in during the async fetch). */
  agentScoped?: boolean;
}

export const ClientNavigation: React.FC<ClientNavigationProps> = ({
  clientId,
  numericClientId,
  clientName,
  activeTab,
  crmLink,
  crmType,
  isSingleAgent = false,
  hasCrmCredentials = true,
  agent,
  agentScoped = false,
}) => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  // The agent drill-down (with or without `agent` resolved yet) has no section tabs.
  const drillDown = Boolean(agent) || agentScoped;

  // Most callers pass only one of the two id shapes; derive the other.
  // Require a positive id: callers pass parseInt(clientId || '0'), so a missing
  // route param yields 0 (and a non-numeric one NaN) — neither is a real client,
  // and both must hide the tabs (don't link to /agentSms/0).
  const cidRaw =
    numericClientId ??
    (clientId && /^\d+$/.test(clientId) ? Number(clientId) : undefined);
  const cid = cidRaw !== undefined && cidRaw > 0 ? cidRaw : undefined;
  const settingsId = clientId ?? (cid !== undefined ? String(cid) : undefined);

  const safeCrmMeta =
    crmType && crmTypeMeta[crmType]
      ? crmTypeMeta[crmType]
      : {
          label: 'Test CRM',
          supportsOnboarding: false,
          supportsSingleAgent: false,
        };

  // Section tabs. Routes mirror the previous button links; the active tab is now
  // highlighted instead of being removed from the row.
  const tabs: SectionTab[] = [
    cid !== undefined && { key: 'sms', label: 'Agent', to: `/agentSms/${cid}` },
    cid !== undefined && {
      key: 'onboarding',
      label: 'Onboarding',
      to: `/onboardingNew/${cid}`,
    },
    cid !== undefined && {
      key: 'leads',
      label: 'Engagements',
      to: `/lead-engagements/${cid}`,
    },
    cid !== undefined && {
      key: 'twilio',
      label: 'Twilio',
      to: `/client/${cid}/twilio-onboarding`,
    },
    settingsId && {
      key: 'settings',
      label: 'Settings',
      to: `/client/${settingsId}/settings`,
    },
  ].filter((t): t is SectionTab => Boolean(t));

  const tabByKey = Object.fromEntries(tabs.map(t => [t.key, t.to]));

  const crmButton = (
    <Tooltip
      title={
        isSingleAgent
          ? 'CRM access is not available for single agents'
          : `Open ${
              agent ? `${agent.first_name} ${agent.last_name}` : clientName
            }'s ${safeCrmMeta.label}`
      }
    >
      <span>
        <HWButton
          disabled={isSingleAgent || !crmLink || !hasCrmCredentials}
          icon={<ExportOutlined />}
          link={crmLink}
          target="_blank"
        >
          {safeCrmMeta.label}
        </HWButton>
      </span>
    </Tooltip>
  );

  // Clicking the client (crumb or title) lands on Settings — the historical default.
  const clientLabel = clientName || 'Client';
  const settingsLink = settingsId
    ? `/client/${settingsId}/settings`
    : undefined;
  const titleText = agent
    ? `${agent.first_name} ${agent.last_name}`
    : clientLabel;
  const titleLink = agent
    ? `/agentSms/${clientId}?agentId=${agent.id}`
    : settingsLink;

  // Breadcrumb is the location indicator; every ancestor (incl. the client) is a link.
  const clientCrumb = agent ? (
    // In the agent drill-down, the client links back to its agent list.
    <Link to={`/agentSms/${clientId}`}>{clientLabel}</Link>
  ) : settingsLink ? (
    <Link to={settingsLink}>{clientLabel}</Link>
  ) : (
    clientLabel
  );
  const crumbs: { title: React.ReactNode }[] = [
    { title: <Link to="/client">Clients</Link> },
    { title: clientCrumb },
  ];
  if (agent) {
    crumbs.push({ title: `${agent.first_name} ${agent.last_name}` });
  }

  const title = (
    <Typography.Title
      level={3}
      style={{ margin: 0, color: token.colorPrimary, fontWeight: 600 }}
    >
      {titleText}
    </Typography.Title>
  );

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 16,
          paddingTop: 12,
        }}
      >
        <Breadcrumb
          items={crumbs}
          style={{ fontSize: 13, justifySelf: 'start', minWidth: 0 }}
        />
        <div
          style={{ justifySelf: 'center', textAlign: 'center', minWidth: 0 }}
        >
          {titleLink ? (
            <Link style={{ textDecoration: 'none' }} to={titleLink}>
              {title}
            </Link>
          ) : (
            title
          )}
        </div>
        <div style={{ justifySelf: 'end', minWidth: 0 }}>{crmButton}</div>
      </div>

      {/* Section tabs are hidden in the per-agent drill-down (agent leads view),
          which is a leaf rather than a sibling of the client sections. */}
      {!drillDown && tabs.length > 0 && (
        <Tabs
          activeKey={activeTab}
          items={tabs.map(t => ({ key: t.key, label: t.label }))}
          onChange={key => tabByKey[key] && navigate(tabByKey[key])}
          style={{ marginTop: 10, marginBottom: 0 }}
        />
      )}
    </div>
  );
};
